import { THEME_ICONS } from './icons';
import { THEME_PART_KEYS, THEME_PARTS, themePartFields } from './parts';
const MAX_SECTIONS_PER_PAGE = 40;
const DEFAULT_TEXT_MAX = 500;
const MULTILINE_TEXT_MAX = 4000;
const RICHTEXT_MAX = 50000;
const HTML_MAX = 50000;
const SCRIPT_TAG = /<script\b/i;
const SECTION_ID = /^[a-z0-9-]{1,40}$/;
const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/** Caminho da própria loja (sem `//host`) ou https. */
export function isSafeThemeLink(value) {
    if (value === '')
        return true;
    if (value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\'))
        return true;
    return /^https:\/\/[^\s]+$/i.test(value);
}
function isSafeImageUrl(value) {
    return value === '' || /^https:\/\/[^\s"'<>]+$/i.test(value);
}
function defaultFor(field) {
    switch (field.type) {
        case 'list':
            return field.default ?? [];
        case 'widget':
        case 'category':
        case 'collection':
            return null;
        case 'toggle':
        case 'range':
        case 'color':
        case 'font':
        case 'select':
        case 'icon':
            return field.default;
        default:
            return field.default ?? '';
    }
}
function normalizeScalar(field, value, path, errors) {
    if (value === undefined)
        return defaultFor(field);
    const invalid = (reason) => {
        errors.push(`${path}: ${reason}`);
        return defaultFor(field);
    };
    switch (field.type) {
        case 'text': {
            if (typeof value !== 'string')
                return invalid('texto esperado');
            const max = field.maxLength ?? (field.multiline ? MULTILINE_TEXT_MAX : DEFAULT_TEXT_MAX);
            if (value.length > max)
                return invalid(`máximo de ${max} caracteres`);
            return value;
        }
        case 'richtext':
            if (typeof value !== 'string')
                return invalid('texto esperado');
            if (value.length > RICHTEXT_MAX)
                return invalid(`máximo de ${RICHTEXT_MAX} caracteres`);
            return value;
        case 'html':
            if (typeof value !== 'string')
                return invalid('texto esperado');
            if (value.length > HTML_MAX)
                return invalid(`máximo de ${HTML_MAX} caracteres`);
            if (SCRIPT_TAG.test(value)) {
                return invalid('script não é permitido no conteúdo da página: use um widget, que roda isolado');
            }
            return value;
        case 'video':
            if (typeof value !== 'string' || !(value === '' || /^https:\/\/[^\s"'<>]+$/i.test(value))) {
                return invalid('vídeo inválido: envie um arquivo ou cole um link https do YouTube ou Vimeo');
            }
            return value;
        case 'icon':
            if (typeof value !== 'string' || !(value in THEME_ICONS))
                return invalid('ícone fora da lista');
            return value;
        case 'image':
            if (typeof value !== 'string' || !isSafeImageUrl(value))
                return invalid('imagem inválida');
            return value;
        case 'color':
            if (typeof value !== 'string' || !HEX_COLOR.test(value))
                return invalid('cor inválida');
            return value;
        case 'font':
            if (typeof value === 'string' && value === '' && field.inheritLabel)
                return value;
            if (typeof value !== 'string' || !field.options.includes(value))
                return invalid('fonte fora da lista');
            return value;
        case 'select':
            if (typeof value !== 'string' || !field.options.some((o) => o.value === value)) {
                return invalid('opção inválida');
            }
            return value;
        case 'toggle':
            if (typeof value !== 'boolean')
                return invalid('liga/desliga esperado');
            return value;
        case 'range': {
            if (typeof value !== 'number' || !Number.isFinite(value))
                return invalid('número esperado');
            if (value < field.min || value > field.max)
                return invalid(`entre ${field.min} e ${field.max}`);
            const step = field.step ?? 1;
            return Math.round((value - field.min) / step) * step + field.min;
        }
        case 'link':
            if (typeof value !== 'string' || !isSafeThemeLink(value.trim()))
                return invalid('link inválido');
            return value.trim();
        case 'widget':
        case 'category':
        case 'collection':
            if (value === null)
                return null;
            if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
                return invalid('item inválido');
            }
            return value;
    }
}
function normalizeField(field, value, path, errors) {
    if (field.type !== 'list')
        return normalizeScalar(field, value, path, errors);
    if (value === undefined)
        return defaultFor(field);
    if (!Array.isArray(value)) {
        errors.push(`${path}: lista esperada`);
        return defaultFor(field);
    }
    if (value.length > field.max)
        errors.push(`${path}: no máximo ${field.max} itens`);
    return value.slice(0, field.max).map((item, index) => {
        const itemPath = `${path}[${index}]`;
        if (!isPlainObject(item)) {
            errors.push(`${itemPath}: item inválido`);
            return normalizeSettings(field.of, {}, itemPath, []);
        }
        return normalizeSettings(field.of, item, itemPath, errors);
    });
}
function normalizeSettings(fields, input, path, errors) {
    const source = isPlainObject(input) ? input : {};
    if (input !== undefined && !isPlainObject(input))
        errors.push(`${path}: objeto esperado`);
    for (const key of Object.keys(source)) {
        if (!(key in fields))
            errors.push(`${path}.${key}: campo desconhecido`);
    }
    const result = {};
    for (const [key, field] of Object.entries(fields)) {
        result[key] = normalizeField(field, source[key], `${path}.${key}`, errors);
    }
    return result;
}
function defaultSections(manifest, page) {
    return (manifest.pages[page]?.defaultSections ?? []).map((section, index) => ({
        id: `${section.type}-${index + 1}`,
        type: section.type,
        settings: normalizeSettings(manifest.sections[section.type]?.settings ?? {}, section.settings ?? {}, `${page}.${section.type}`, []),
    }));
}
function normalizePage(manifest, page, input, errors) {
    if (input === undefined)
        return defaultSections(manifest, page);
    if (!Array.isArray(input)) {
        errors.push(`pages.${page}: lista de seções esperada`);
        return defaultSections(manifest, page);
    }
    if (input.length > MAX_SECTIONS_PER_PAGE) {
        errors.push(`pages.${page}: no máximo ${MAX_SECTIONS_PER_PAGE} seções`);
    }
    const definition = manifest.pages[page];
    const seenIds = new Set();
    const seenUnique = new Set();
    const sections = [];
    input.slice(0, MAX_SECTIONS_PER_PAGE).forEach((raw, index) => {
        const path = `pages.${page}[${index}]`;
        if (!isPlainObject(raw) || typeof raw.type !== 'string') {
            errors.push(`${path}: seção inválida`);
            return;
        }
        const sectionDef = manifest.sections[raw.type];
        if (!sectionDef || !definition.allowedSections.includes(raw.type)) {
            errors.push(`${path}: seção "${raw.type}" não existe nesta página`);
            return;
        }
        if (sectionDef.unique && seenUnique.has(raw.type)) {
            errors.push(`${path}: só pode haver uma seção "${sectionDef.label}"`);
            return;
        }
        seenUnique.add(raw.type);
        let id = typeof raw.id === 'string' && SECTION_ID.test(raw.id) ? raw.id : '';
        if (!id || seenIds.has(id)) {
            if (raw.id !== undefined)
                errors.push(`${path}: identificador inválido ou repetido`);
            id = `${raw.type}-${index + 1}`;
            while (seenIds.has(id))
                id = `${id}-x`;
        }
        seenIds.add(id);
        sections.push({
            id,
            type: raw.type,
            ...(raw.hidden === true ? { hidden: true } : {}),
            settings: normalizeSettings(sectionDef.settings, raw.settings, `${path}.settings`, errors),
        });
    });
    return sections;
}
/** Páginas cujas seções moram no conteúdo do tema (as demais, como `page`, moram fora). */
const THEME_CONTENT_PAGES = ['home', 'sidebar', 'product', 'collection'];
/**
 * Conteúdo gravado antes das partes: um `settings` só, com as cores, a fonte e a
 * disposição. Cada valor vai para a parte que tem o campo de mesmo nome, sem erro: é
 * um formato antigo válido, não um conteúdo quebrado, e assim nenhuma loja precisa de
 * migração de dados. O próximo rascunho salvo já sai no formato novo.
 */
function partsFromLegacySettings(settings) {
    const parts = {};
    for (const [key, value] of Object.entries(settings)) {
        const part = THEME_PART_KEYS.find((candidate) => key in THEME_PARTS[candidate].fields);
        if (part)
            parts[part] = { ...parts[part], [key]: value };
    }
    return parts;
}
export function validateThemeContent(manifest, input) {
    const errors = [];
    const source = isPlainObject(input) ? input : {};
    if (input !== undefined && input !== null && !isPlainObject(input))
        errors.push('conteúdo inválido');
    const pagesInput = isPlainObject(source.pages) ? source.pages : {};
    const pages = {};
    for (const page of THEME_CONTENT_PAGES) {
        if (manifest.pages[page])
            pages[page] = normalizePage(manifest, page, pagesInput[page], errors);
    }
    let partsInput = {};
    if (isPlainObject(source.parts)) {
        partsInput = source.parts;
        for (const key of Object.keys(partsInput)) {
            if (!(key in THEME_PARTS))
                errors.push(`parts.${key}: parte desconhecida`);
        }
    }
    else if (source.parts !== undefined) {
        errors.push('parts: objeto esperado');
    }
    else if (isPlainObject(source.settings)) {
        partsInput = partsFromLegacySettings(source.settings);
    }
    const parts = {};
    for (const part of THEME_PART_KEYS) {
        parts[part] = normalizeSettings(themePartFields(manifest, part), partsInput[part], `parts.${part}`, errors);
    }
    return { content: { parts, pages }, errors };
}
/**
 * Seções de uma página fora do conteúdo do tema (página institucional): mesma
 * normalização, contra o que o manifesto aceita naquele tipo de página.
 */
export function validateThemeSections(manifest, page, input) {
    const errors = [];
    if (!manifest.pages[page])
        return { sections: [], errors: [`página "${page}" não existe no tema`] };
    return { sections: normalizePage(manifest, page, input, errors), errors };
}
/** Conteúdo com que o tema nasce: padrões do manifesto. */
export function defaultThemeContent(manifest) {
    return validateThemeContent(manifest, {}).content;
}
/**
 * Valores de todos os campos de um tipo (ex.: os widgets e categorias que as seções
 * citam), para quem precisa buscar esses itens antes de desenhar. Seção escondida fica
 * de fora.
 */
function collectInto(found, fields, values, type) {
    for (const [key, field] of Object.entries(fields)) {
        const value = values?.[key];
        if (field.type === type && typeof value === 'number')
            found.add(value);
        if (field.type === 'list' && Array.isArray(value)) {
            value.forEach((item) => collectInto(found, field.of, (item ?? {}), type));
        }
    }
}
/** Referências nas seções de uma página (inicial ou institucional). */
export function collectSectionReferences(manifest, sections, type) {
    const found = new Set();
    for (const section of sections) {
        if (section.hidden)
            continue;
        collectInto(found, manifest.sections[section.type]?.settings ?? {}, section.settings, type);
    }
    return [...found];
}
/** Referências nas partes e nas páginas do conteúdo do tema. */
export function collectThemeReferences(manifest, content, type) {
    const found = new Set();
    for (const part of THEME_PART_KEYS) {
        collectInto(found, THEME_PARTS[part].fields, content.parts?.[part] ?? {}, type);
    }
    for (const sections of Object.values(content.pages)) {
        collectSectionReferences(manifest, sections ?? [], type).forEach((id) => found.add(id));
    }
    return [...found];
}
//# sourceMappingURL=validate.js.map